// src/notifications/notificationRouting.js
// Shared routing helpers for Loop push notifications.
// Keep this file dependency-light so it can be used from App and native push listeners.

function clean(value) {
  return String(value || "").trim();
}

function lower(value) {
  return clean(value).toLowerCase();
}

function encode(value) {
  return encodeURIComponent(clean(value));
}

function appendParams(path, params = {}) {
  const entries = Object.entries(params).filter(([, value]) => value !== undefined && value !== null && clean(value) !== "");
  if (!entries.length) return path;
  const qs = entries.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(clean(value))}`).join("&");
  return `${path}${path.includes("?") ? "&" : "?"}${qs}`;
}


function isClientLikeTarget(value) {
  const role = lower(value);
  return Boolean(role && ["client", "customer", "cliente", "usuario", "user", "consumer"].some((part) => role.includes(part)));
}

function routeToBusinessCalendar(businessParam, params = {}) {
  if (businessParam) return appendParams(`/business/${encode(businessParam)}/calendar`, params);
  return appendParams("/open-calendar", params);
}

function normalizeRelativeRoute(value) {
  const raw = clean(value);
  if (!raw) return "";

  if (raw.startsWith("/")) return raw;

  try {
    const url = new URL(raw);
    if (typeof window !== "undefined" && url.origin === window.location.origin) {
      return `${url.pathname || "/"}${url.search || ""}${url.hash || ""}`;
    }
    return raw;
  } catch (_) {
    return "";
  }
}


function appendMissingParams(path, params = {}) {
  const raw = clean(path);
  if (!raw) return raw;

  // Do not mutate external absolute URLs. Relative routes and same-origin URLs
  // can be enriched safely with bookingId/businessId/source for deep links.
  if (/^https?:\/\//i.test(raw)) {
    try {
      const url = new URL(raw);
      if (typeof window === "undefined" || url.origin !== window.location.origin) return raw;
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || clean(value) === "") return;
        if (!url.searchParams.has(key)) url.searchParams.set(key, clean(value));
      });
      return `${url.pathname || "/"}${url.search || ""}${url.hash || ""}`;
    } catch (_) {
      return raw;
    }
  }

  try {
    const url = new URL(raw, "https://loop.local");
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || clean(value) === "") return;
      if (!url.searchParams.has(key)) url.searchParams.set(key, clean(value));
    });
    return `${url.pathname || "/"}${url.search || ""}${url.hash || ""}`;
  } catch (_) {
    return appendParams(raw, params);
  }
}

function enrichExplicitNotificationRoute(explicitRoute, data = {}) {
  const explicit = normalizeRelativeRoute(explicitRoute);
  if (!explicit) return "";

  const type = lower(data.type || data.notificationType || data.eventType || data.action);
  const businessId = clean(data.businessId);
  const bookingId = clean(data.bookingId || data.reservationId || data.appointmentId);
  const targetRole = clean(data.targetRole || data.role || data.audience);
  const baseParams = { bookingId, businessId, source: clean(data.source) || "push" };

  if (["review_request", "rating_request", "post_service_review"].includes(type)) {
    return appendMissingParams(explicit, baseParams);
  }

  if (type.includes("payment") || type.includes("pago") || type.includes("paid") || type.includes("refund") || type.includes("reembolso") || type.includes("wompi")) {
    return appendMissingParams(explicit, { panel: "payments", ...baseParams });
  }

  if (type.includes("inventory") || type.includes("inventario") || type.includes("stock")) {
    return appendMissingParams(explicit, { panel: "inventory", businessId, source: clean(data.source) || "push" });
  }

  if (type.includes("membership") || type.includes("membres") || type.includes("loyalty") || type.includes("fidelidad") || type.includes("reward") || type.includes("recompensa") || type.includes("benefit") || type.includes("beneficio")) {
    return appendMissingParams(explicit, { panel: isClientLikeTarget(targetRole) ? "benefits" : (type.includes("loyalty") || type.includes("fidelidad") ? "loyalty" : "memberships"), ...baseParams });
  }

  if (["new_booking_request", "booking_pending", "pending_booking", "reservation_pending", "worker_assigned_booking", "worker_booking", "worker_reminder", "worker_reminder_2h", "booking_confirmed", "booking_rejected", "booking_cancelled", "booking_canceled", "booking_updated", "booking_reminder", "booking_reminder_24h", "booking_reminder_2h"].includes(type)) {
    return appendMissingParams(explicit, baseParams);
  }

  return appendMissingParams(explicit, baseParams);
}


const LOOP_LAST_ROUTE_STORAGE_KEY = "loop_push_last_route_v1";

function persistLoopNotificationRouteDebug(snapshot = {}) {
  try {
    if (typeof window === "undefined") return snapshot;
    const safeSnapshot = {
      route: snapshot.route || "",
      data: snapshot.data && typeof snapshot.data === "object" ? snapshot.data : {},
      time: snapshot.time || new Date().toISOString(),
      source: snapshot.source || "notification",
    };
    window.__LOOP_PUSH_LAST_ROUTE__ = safeSnapshot;
    try { window.localStorage?.setItem?.(LOOP_LAST_ROUTE_STORAGE_KEY, JSON.stringify(safeSnapshot)); } catch (_) {}
    try { window.sessionStorage?.setItem?.(LOOP_LAST_ROUTE_STORAGE_KEY, JSON.stringify(safeSnapshot)); } catch (_) {}
    return safeSnapshot;
  } catch (_) {
    return snapshot;
  }
}

function restoreLoopNotificationRouteDebug() {
  try {
    if (typeof window === "undefined" || window.__LOOP_PUSH_LAST_ROUTE__) return;
    const raw = window.localStorage?.getItem?.(LOOP_LAST_ROUTE_STORAGE_KEY) || window.sessionStorage?.getItem?.(LOOP_LAST_ROUTE_STORAGE_KEY) || "";
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") window.__LOOP_PUSH_LAST_ROUTE__ = parsed;
  } catch (_) {}
}

restoreLoopNotificationRouteDebug();

export function getLoopNotificationData(rawEventOrData = {}) {
  const event = rawEventOrData || {};
  const direct = event.data && typeof event.data === "object" ? event.data : null;
  const notification = event.notification && typeof event.notification === "object" ? event.notification : null;
  const notificationData = notification && notification.data && typeof notification.data === "object" ? notification.data : null;
  const actionData = event.action && event.action.data && typeof event.action.data === "object" ? event.action.data : null;

  return {
    ...(notificationData || {}),
    ...(actionData || {}),
    ...(direct || {}),
    ...(event && !direct && !notification ? event : {}),
  };
}

export function buildLoopNotificationRoute(rawData = {}) {
  const data = getLoopNotificationData(rawData);
  const explicit = enrichExplicitNotificationRoute(data.route || data.url || data.deepLink || data.link, data);

  const type = lower(data.type || data.notificationType || data.eventType || data.action);
  const businessParam = clean(data.businessSlug || data.publicSlug || data.businessRouteParam || data.businessId);
  const businessId = clean(data.businessId);
  const bookingId = clean(data.bookingId || data.reservationId || data.appointmentId);
  const targetRole = clean(data.targetRole || data.role || data.audience);

  if (explicit) return explicit;

  if (["new_booking_request", "booking_pending", "pending_booking", "reservation_pending"].includes(type)) {
    if (businessParam) {
      return appendParams(`/business/${encode(businessParam)}/calendar`, {
        panel: "pending",
        bookingId,
        businessId,
        source: "push",
      });
    }
    return appendParams("/admin/pending-reservations", { bookingId, businessId, source: "push" });
  }

  if (["worker_assigned_booking", "worker_booking", "worker_reminder", "worker_reminder_2h"].includes(type)) {
    if (businessParam) {
      return appendParams(`/business/${encode(businessParam)}/calendar`, { bookingId, businessId, source: "push" });
    }
    return appendParams("/open-calendar", { bookingId, businessId, source: "push" });
  }

  if (["review_request", "rating_request", "post_service_review"].includes(type)) {
    return appendParams("/calificar-servicios", { bookingId, businessId, source: "push" });
  }

  if (type.includes("payment") || type.includes("pago") || type.includes("paid") || type.includes("refund") || type.includes("reembolso") || type.includes("wompi")) {
    if (isClientLikeTarget(targetRole)) {
      return appendParams("/my-bookings", { panel: "payments", bookingId, businessId, source: "push" });
    }
    return routeToBusinessCalendar(businessParam, { panel: "payments", bookingId, businessId, source: "push" });
  }

  if (type.includes("inventory") || type.includes("inventario") || type.includes("stock")) {
    return routeToBusinessCalendar(businessParam, { panel: "inventory", businessId, source: "push" });
  }

  if (type.includes("membership") || type.includes("membres") || type.includes("loyalty") || type.includes("fidelidad") || type.includes("reward") || type.includes("recompensa") || type.includes("benefit") || type.includes("beneficio")) {
    if (isClientLikeTarget(targetRole)) {
      return appendParams("/my-bookings", { panel: "benefits", bookingId, businessId, source: "push" });
    }
    return routeToBusinessCalendar(businessParam, { panel: type.includes("loyalty") || type.includes("fidelidad") ? "loyalty" : "memberships", bookingId, businessId, source: "push" });
  }

  if (["booking_confirmed", "booking_rejected", "booking_cancelled", "booking_canceled", "booking_updated", "booking_reminder", "booking_reminder_24h", "booking_reminder_2h"].includes(type)) {
    return appendParams("/my-bookings", { bookingId, businessId, source: "push" });
  }

  if (businessParam) {
    return appendParams(`/business/${encode(businessParam)}/calendar`, { bookingId, businessId, source: "push" });
  }

  return "/";
}

export function openLoopNotificationRoute(rawData = {}, { navigate = null, replace = false } = {}) {
  const route = buildLoopNotificationRoute(rawData);
  const data = getLoopNotificationData(rawData);

  persistLoopNotificationRouteDebug({
    route,
    data,
    time: new Date().toISOString(),
    source: "openLoopNotificationRoute",
  });

  if (typeof navigate === "function") {
    navigate(route, { replace });
    return route;
  }

  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent("loop:push-open", { detail: { route, data } }));
      return route;
    } catch (_) {}

    if (route.startsWith("http")) window.location.assign(route);
    else window.location.assign(route || "/");
  }

  return route;
}
